'use client';

import { c as _c } from "react/compiler-runtime";
import { summarizeIceStats } from '@shared/utils/helpers/iceStats';
import type { DataChannelInterface } from '@shared/utils/helpers/remoteBoardAgent';
import { type IceServer, STUN_ONLY } from '@shared/utils/hooks/useIceServers';
import socket from '@shared/utils/socket/socket';
import { useEffect, useState, useSyncExternalStore } from 'react';
const subscribeSocketConnected = (cb: () => void) => {
  socket.on('connect', cb);
  socket.on('disconnect', cb);
  return () => {
    socket.off('connect', cb);
    socket.off('disconnect', cb);
  };
};
const getSocketConnected = () => socket.connected;
export type RunnerConnectionState = 'idle' | 'connecting' | 'connected' | 'failed';
export type RunnerChannel = DataChannelInterface;

// `channel` identity only changes when the peer connects or tears down, so
// downstream hooks can key on it without rebuilding every render.
/** What the handshake actually did, so a failure can say why rather than
 *  leaving a spinner. Read by the runner selector when a link fails. */
export type RunnerLinkDiagnostics = {
  /** Candidate types we gathered: host (LAN), srflx (STUN), relay (TURN). */
  localCandidateTypes: string[];
  /** Candidate types the runner sent us. Empty means it never reached us. */
  remoteCandidateTypes: string[];
  /** Whether the runner's answer arrived at all. */
  answerReceived: boolean;
  iceConnectionState: string;
  /** What the candidate pairs actually did, read off getStats() when the
   *  handshake gives up. Candidate types alone say both sides had a route on
   *  paper; this says whether any check was answered. Null until then. */
  pairSummary: string | null;
  /** How the runner's candidates were received: added successfully, versus
   *  rejected by the ICE agent. A candidate that arrives but will not add is
   *  invisible otherwise — the types still show up as "received" and the link
   *  simply never connects. */
  candidatesAdded: number;
  candidatesRejected: number;
  /** Set when the server refused to relay, or the handshake timed out. */
  failure: string | null;
};

/**
 * How long to let a handshake run before calling it dead.
 *
 * This was 15s, which is fine for two peers on one network — they pair on a
 * host candidate almost at once. It is not enough for a relayed connection:
 * ICE checks candidate pairs in priority order, so it works through every
 * private address the other side offered (LAN, Tailscale, link-local IPv6)
 * before it ever reaches the relay pair. Giving up mid-check closed the peer
 * and made the runner report a failure it had not actually had.
 */
const HANDSHAKE_TIMEOUT_MS = 45_000;
const EMPTY_DIAGNOSTICS: RunnerLinkDiagnostics = {
  localCandidateTypes: [],
  remoteCandidateTypes: [],
  answerReceived: false,
  iceConnectionState: 'new',
  pairSummary: null,
  candidatesAdded: 0,
  candidatesRejected: 0,
  failure: null
};

/** `candidate:842163049 1 udp 1686052607 1.2.3.4 51502 typ srflx ...` */
const candidateType = (candidate: string): string => /\btyp (\w+)/.exec(candidate)?.[1] ?? 'unknown';

/**
 * Fill in what the peer left null, before the candidate reaches the native layer.
 *
 * The runner signals `sdpMLineIndex: null`, which browsers accept happily —
 * sdpMid identifies the m-line on its own. react-native-webrtc on the New
 * Architecture passes the object into codegen'd JSI bindings that type-check
 * it, and a null where a number is declared throws "Exception in HostFunction".
 * Debug builds tolerated it and release builds did not, so every remote
 * candidate was rejected in shipped apps only: the ICE agent never saw one and
 * the link sat at checking until it timed out. Defaulting the index to 0 loses
 * nothing, and every runner already deployed sends null, so this cannot be
 * fixed from the runner side alone.
 */
const toCandidateInit = (init: RTCIceCandidateInit) => ({
  candidate: init.candidate ?? '',
  sdpMLineIndex: init.sdpMLineIndex ?? 0,
  sdpMid: init.sdpMid ?? '0'
});

/** One candidate on the wire, in the shape the signalling server relays. */
type OutboundCandidate = {
  candidate: string;
  sdpMLineIndex: number | null;
  sdpMid: string | null;
};
export function useRunnerConnection(runnerSocketId, iceServers, attempt, report) {
  const $ = _c(16);
  const [state, setState] = useState("idle");
  const [channel, setChannel] = useState(null);
  const [stream, setStream] = useState(null);
  const [diagnostics, setDiagnostics] = useState(EMPTY_DIAGNOSTICS);
  const socketConnected = useSyncExternalStore(subscribeSocketConnected, getSocketConnected, _temp);
  let t0;
  if ($[0] !== iceServers || $[1] !== report || $[2] !== runnerSocketId || $[3] !== socketConnected) {
    t0 = () => {
      if (!runnerSocketId || !socketConnected) {
        setState("idle");
        setChannel(null);
        setStream(null);
        return;
      }
      if (!iceServers) {
        setState("connecting");
        return;
      }
      setState("connecting");
      setDiagnostics(EMPTY_DIAGNOSTICS);
      const linkId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const diag = {
        ...EMPTY_DIAGNOSTICS,
        localCandidateTypes: [],
        remoteCandidateTypes: []
      };
      const publish = () => setDiagnostics({
        ...diag
      });
      const describe = () => [`ice=${diag.iceConnectionState} answer=${diag.answerReceived ? "received" : "never"}`, `local=${diag.localCandidateTypes.join(",") || "none"}`, `runner=${diag.remoteCandidateTypes.join(",") || "none"}`, `added=${diag.candidatesAdded} rejected=${diag.candidatesRejected}`, `pairs=${diag.pairSummary ?? "none"}`, `failure=${diag.failure ?? "none"}`].join("\n");
      const peer = new RTCPeerConnection({
        iceServers: iceServers.length > 0 ? iceServers : STUN_ONLY
      });
      const addCandidate = init => {
        Promise.resolve(peer.addIceCandidate(new RTCIceCandidate(toCandidateInit(init)))).then(() => {
          diag.candidatesAdded = diag.candidatesAdded + 1;
          publish();
        }).catch(error => {
          diag.candidatesRejected = diag.candidatesRejected + 1;
          diag.failure = diag.failure ?? `candidate rejected: ${String(error)}`;
          publish();
        });
      };
      let remoteDescSet = false;
      const pendingCandidates = [];
      const handlers = new Set();
      const closeHandlers = new Set();
      let closed = false;
      const fireClosed = () => {
        if (closed) {
          return;
        }
        closed = true;
        for (const handler of closeHandlers) {
          handler();
        }
      };
      const earlyMessages = [];
      try {
        peer.addTransceiver("video", {
          direction: "recvonly"
        });
        peer.ontrack = event => setStream(event.streams[0] ?? new MediaStream([event.track]));
      } catch {}
      const dc = peer.createDataChannel("card-ai");
      const stableChannel = {
        send: msg => {
          if (dc.readyState !== "open") {
            return;
          }
          try {
            dc.send(msg);
          } catch {}
        },
        onMessage: handler_0 => {
          handlers.add(handler_0);
          if (earlyMessages.length > 0) {
            const drain = earlyMessages.splice(0);
            for (const msg_0 of drain) {
              handler_0(msg_0);
            }
          }
          return () => {
            handlers.delete(handler_0);
          };
        },
        onClose: handler_1 => {
          if (closed) {
            handler_1();
            return _temp2;
          }
          closeHandlers.add(handler_1);
          return () => {
            closeHandlers.delete(handler_1);
          };
        }
      };
      dc.onopen = () => {
        setState("connected");
        setChannel(stableChannel);
        report?.("connected", linkId, runnerSocketId, describe());
      };
      dc.onclose = () => {
        fireClosed();
        setState("idle");
        setChannel(null);
        setStream(null);
      };
      dc.onmessage = event_0 => {
        const msg_1 = typeof event_0.data === "string" ? event_0.data : "";
        if (handlers.size === 0) {
          earlyMessages.push(msg_1);
          return;
        }
        for (const handler_2 of handlers) {
          handler_2(msg_1);
        }
      };
      let offerSent = false;
      const outboundCandidates = [];
      const emitCandidate = candidate => socket.emit("runnerIceCandidate", {
        targetSocketId: runnerSocketId,
        candidate
      });
      peer.onicecandidate = event_1 => {
        if (event_1.candidate) {
          const type = candidateType(event_1.candidate.candidate);
          if (!diag.localCandidateTypes.includes(type)) {
            diag.localCandidateTypes.push(type);
            publish();
          }
          const candidate_0 = {
            candidate: event_1.candidate.candidate,
            sdpMLineIndex: event_1.candidate.sdpMLineIndex ?? null,
            sdpMid: event_1.candidate.sdpMid ?? null
          };
          if (offerSent) {
            emitCandidate(candidate_0);
          } else {
            outboundCandidates.push(candidate_0);
          }
        }
      };
      let statsCaptured = false;
      const captureStats = () => {
        if (statsCaptured) {
          return Promise.resolve();
        }
        statsCaptured = true;
        return peer.getStats().then(statsReport => {
          diag.pairSummary = summarizeIceStats(statsReport) ?? "report held no candidate pairs";
          publish();
        }).catch(error_0 => {
          diag.pairSummary = `stats unavailable: ${String(error_0)}`;
          publish();
        });
      };
      peer.oniceconnectionstatechange = () => {
        diag.iceConnectionState = peer.iceConnectionState;
        publish();
        if (peer.iceConnectionState === "failed") {
          captureStats();
        }
      };
      peer.onconnectionstatechange = () => {
        const peerState = peer.connectionState;
        if (peerState === "failed") {
          setState("failed");
        }
        if (peerState === "failed" || peerState === "closed") {
          fireClosed();
          setChannel(null);
          setStream(null);
        }
      };
      const onAnswer = data => {
        if (data.fromSocketId !== runnerSocketId) {
          return;
        }
        diag.answerReceived = true;
        publish();
        peer.setRemoteDescription(new RTCSessionDescription(data.answer)).then(() => {
          remoteDescSet = true;
          for (const c of pendingCandidates) {
            addCandidate(c);
          }
          pendingCandidates.length = 0;
        }).catch(error_1 => {
          diag.failure = `answer rejected: ${String(error_1)}`;
          publish();
          setState("failed");
        });
      };
      const onIceCandidate = data_0 => {
        if (data_0.fromSocketId !== runnerSocketId) {
          return;
        }
        const type_0 = candidateType(data_0.candidate.candidate);
        if (!diag.remoteCandidateTypes.includes(type_0)) {
          diag.remoteCandidateTypes.push(type_0);
          publish();
        }
        if (remoteDescSet) {
          addCandidate(data_0.candidate);
        } else {
          pendingCandidates.push(data_0.candidate);
        }
      };
      const onSignalError = data_1 => {
        if (data_1.targetSocketId !== runnerSocketId) {
          return;
        }
        diag.failure = `server refused the offer: ${data_1.reason}`;
        publish();
        setState("failed");
      };
      socket.on("runnerAnswer", onAnswer);
      socket.on("runnerIceCandidate", onIceCandidate);
      socket.on("runnerSignalError", onSignalError);
      peer.createOffer().then(offer => peer.setLocalDescription(offer)).then(() => {
        if (peer.localDescription) {
          socket.emit("runnerOffer", {
            targetSocketId: runnerSocketId,
            linkId,
            offer: {
              type: peer.localDescription.type as 'offer',
              sdp: peer.localDescription.sdp
            }
          });
          offerSent = true;
          for (const candidate_1 of outboundCandidates) {
            emitCandidate(candidate_1);
          }
          outboundCandidates.length = 0;
        }
      }).catch(() => {
        setState("failed");
      });
      const timeout = setTimeout(() => {
        if (dc.readyState !== "open") {
          diag.failure = diag.failure ?? `gave up after ${HANDSHAKE_TIMEOUT_MS / 1000}s`;
          publish();
          setState("failed");
          captureStats().finally(() => report?.("failed", linkId, runnerSocketId, describe()));
        }
      }, HANDSHAKE_TIMEOUT_MS);
      return () => {
        clearTimeout(timeout);
        socket.off("runnerAnswer", onAnswer);
        socket.off("runnerIceCandidate", onIceCandidate);
        socket.off("runnerSignalError", onSignalError);
        fireClosed();
        dc.close();
        peer.close();
        setChannel(null);
        setStream(null);
        setState("idle");
      };
    };
    $[0] = iceServers;
    $[1] = report;
    $[2] = runnerSocketId;
    $[3] = socketConnected;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== attempt || $[6] !== iceServers || $[7] !== report || $[8] !== runnerSocketId || $[9] !== socketConnected) {
    t1 = [runnerSocketId, socketConnected, iceServers, report, attempt];
    $[5] = attempt;
    $[6] = iceServers;
    $[7] = report;
    $[8] = runnerSocketId;
    $[9] = socketConnected;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  useEffect(t0, t1);
  let t2;
  if ($[11] !== channel || $[12] !== diagnostics || $[13] !== state || $[14] !== stream) {
    t2 = {
      state,
      channel,
      diagnostics,
      stream
    };
    $[11] = channel;
    $[12] = diagnostics;
    $[13] = state;
    $[14] = stream;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}
function _temp2() {}
function _temp() {
  return false;
}